const lootboxSlasherImage = new URL('../assets/lootbox_slasher.png', import.meta.url).href

const SLASHER_COLLECTION = 'CQsVrYkv52tLB7howZAhbUZGBku5ysRH1Xmv5mq6LkPZ'

const ASSETS = 'https://assets.bravoready.com/br1-infinite/images'

/** Thumbnail for an inventory item. Returns undefined for types we have no artwork for.
 *
 *  Drawn from `art_operative_id` where the server sends one. A skin's own id is
 *  in the reserved range above 900000 and has no artwork at all, so it borrows
 *  the picture of the weapon it was made from -- which is what it looks like. */
export const assetUrl = (nft: NFT): string | undefined => {
    const id = (nft as NFT & { art_operative_id?: number }).art_operative_id ?? nft.operative_id
    switch (nft.operative_type) {
        case 'ape':
        case 'droid':
            return `${ASSETS}/webp/min/${nft.operative_type}s/${id}-pfp.webp`
        case 'lootbox':
            if (nft.collection === SLASHER_COLLECTION) return lootboxSlasherImage
            return `${ASSETS}/png/loot-boxes/loot-box.png`
        case 'weapon':
            if (id >= 15000) return `${ASSETS}/webp/min/weapons-v2/${id}.webp`
            return `${ASSETS}/webp/min/weapons/${id}.webp`
        default:
            return undefined
    }
}

/** The animated version of the same artwork, where one exists.
 *
 *  Deliberately not used for the inventory grid: these are full-size gifs and
 *  a wallet of 600 would fetch 600 of them. The detail panel shows one item, so
 *  it can afford the real thing.
 *
 *  A weapon's gif is `{id}.gif` under its collection's folder -- no `-pfp`
 *  suffix, unlike an operative's, and split at 15000 the way the still is.
 *  Getting either of those wrong returns a 404 rather than an error, so it
 *  shows as a broken image.
 */
export const assetAnimationUrl = (nft: NFT): string | undefined => {
    const id = (nft as NFT & { art_operative_id?: number }).art_operative_id ?? nft.operative_id
    switch (nft.operative_type) {
        case 'ape':
        case 'droid':
            return `${ASSETS}/gif/${nft.operative_type}s/${id}-pfp.gif`
        case 'weapon':
            if (id >= 15000) return `${ASSETS}/gif/weapons-v2/${id}.gif`
            return `${ASSETS}/gif/weapons/${id}.gif`
        default:
            return undefined
    }
}

/** Same artwork, addressed by type + id, for previews that have no NFT record behind them. */
export const assetUrlFor = (operative_type: string, operative_id: number, collection?: string): string | undefined =>
    assetUrl({ operative_type, operative_id, collection } as NFT)

export const ITEM_LABELS: Record<string, string> = {
    ape: 'Ape',
    droid: 'Droid',
    lootbox: 'Loot Box',
    weapon: 'Weapon',
}

export const itemLabel = (type: string) => ITEM_LABELS[type] ?? 'Operative'


/** What to call an inventory item.
 *
 *  Its own name where the server sends one -- a skin is called something like
 *  "Neon Reaper", and falling back to "Weapon #900007" printed a number from
 *  the reserved skin range that names nothing anybody recognises. Older
 *  responses have no name, so the type and id remain the fallback.
 */
export const itemName = (nft: NFT): string => {
    const named = (nft as NFT & { name?: string }).name
    if (named && named.trim()) return named
    return `${itemLabel(nft.operative_type)} #${nft.operative_id}`
}
